import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Logo from './Logo';

/**
 * The menu is permission-driven: each entry declares the permission it needs,
 * and an entry the signed-in account cannot use simply is not rendered. A staff
 * login therefore sees only the modules its Role opens up.
 */
const MENU = [
  {
    section: 'Overview',
    items: [
      { to: '/', label: 'Dashboard', icon: '▦', permission: 'dashboard.view', end: true },
      { to: '/live-map', label: 'Live Map', icon: '◎', permission: 'dispatch.view' },
    ],
  },
  {
    section: 'Operations',
    items: [
      { to: '/orders', label: 'Orders', icon: '☰', permission: 'order.view' },
      { to: '/customers', label: 'Customers', icon: '☺', permission: 'customer.view' },
      { to: '/partners', label: 'Delivery Partners', icon: '⛟', permission: 'partner.view' },
      { to: '/payments', label: 'Payments', icon: '₹', permission: 'payment.view' },
    ],
  },
  {
    section: 'Catalogue',
    items: [
      { to: '/services', label: 'Services', icon: '◈', permission: 'service.view' },
      { to: '/pricing', label: 'Pricing', icon: '⇅', permission: 'pricing.view' },
      { to: '/packages', label: 'Packages', icon: '📦', permission: 'package.view' },
      { to: '/coupons', label: 'Coupons', icon: '%', permission: 'coupon.view' },
      { to: '/holidays', label: 'Holidays & Schedule', icon: '📅', permission: 'holiday.view' },
      { to: '/banners', label: 'Banners', icon: '▤', permission: 'banner.view' },
    ],
  },
  {
    section: 'Administration',
    items: [
      { to: '/stores', label: 'Stores', icon: '⌂', permission: 'store.view' },
      { to: '/locations', label: 'Locations', icon: '🌍', permission: 'location.view' },
      { to: '/staff', label: 'Staff', icon: '👥', permission: 'staff.view' },
      { to: '/roles', label: 'Roles & Permissions', icon: '🔑', permission: 'role.view' },
      { to: '/notifications', label: 'Notifications', icon: '🔔', permission: 'notification.view' },
      { to: '/reports', label: 'Reports', icon: '📈', permission: 'report.view' },
      { to: '/cms', label: 'CMS Pages', icon: '📄', permission: 'cms.view' },
      { to: '/settings', label: 'Settings', icon: '⚙', permission: 'settings.view' },
    ],
  },
];

export default function Sidebar({ open, onNavigate }) {
  const { can, admin, storeName } = useAuth();

  return (
    <aside className={`sidebar ${open ? 'open' : ''}`}>
      <div className="sidebar-brand">
        <Logo size={34} light />
      </div>

      <nav className="sidebar-nav">
        {MENU.map((group) => {
          const visible = group.items.filter((item) => can(item.permission));
          if (!visible.length) return null;

          return (
            <div key={group.section} className="nav-group">
              <div className="nav-section">{group.section}</div>
              {visible.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={onNavigate}
                  className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                >
                  <span className="nav-icon">{item.icon}</span>
                  {item.label}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>

      <div className="sidebar-foot">
        <div className="sf-name">{admin?.name}</div>
        <div className="sf-role">
          {admin?.isSuperAdmin ? 'Super Admin' : admin?.role?.name || 'Staff'}
          {storeName ? ` · ${storeName}` : ''}
        </div>
      </div>
    </aside>
  );
}
